import * as React from "react";

import { useGtm } from "../../../Common";

import { ReactPlpAnalyticsInterface } from "./analytics-interface";

export const useAnalyticsGtm = () => {
  const { pushToTheDataLayer, consumerInitializationGuard } = useGtm();

  const onViewFredhopperListingPage: NonNullable<
    ReactPlpAnalyticsInterface["onViewFredhopperListingPage"]
  > = React.useCallback(
    ({ initialStateSearchTerms, fredhopperResponse }) => {
      let event = {
        event: "onViewFredhopperListingPage",
        target: {
          page: `${window.location.pathname}${window.location.search}`,
        },
        sourceId: fredhopperResponse.response_id,
      };

      if ((initialStateSearchTerms?.length || 0) > 0) {
        const eventTargetingSearchTerms = {
          "search-terms": initialStateSearchTerms,
        };
        event.target = {
          ...event.target,
          ...eventTargetingSearchTerms,
        };
      }

      let ga4Event = {
          event: 'view_item_list',
          ecommerce: {
              item_list_id: fredhopperResponse.breadcrumbs[fredhopperResponse.breadcrumbs.length - 1]?.id,
              item_list_name: fredhopperResponse.breadcrumbs[fredhopperResponse.breadcrumbs.length - 1]?.name,
              items: fredhopperResponse.products?.map((item, index) => ({
                  item_id: item.id,
                  item_name: item.heading,
                  index,
                  price: item.special_price || item.price
              }))
          }
      };

      pushToTheDataLayer(event);
      pushToTheDataLayer(ga4Event);
    },
    [pushToTheDataLayer]
  );

  const clickActionOnProduct: NonNullable<
    ReactPlpAnalyticsInterface["clickActionOnProduct"]
  > = React.useCallback(
    ({ presentationProduct }) => {
      const event = {
        event: "clickActionOnProduct",
        target: {
          product: presentationProduct.id,
        },
        sourceId: presentationProduct.fredhopperResponseId,
      };
      pushToTheDataLayer(event);
    },
    [pushToTheDataLayer]
  );

  const clickActionOnProductFromCampaign: NonNullable<
    ReactPlpAnalyticsInterface["clickActionOnProductFromCampaign"]
  > = React.useCallback(
    ({ presentationThemeProduct }) => {
      const event = {
        event: "clickActionOnProductFromCampaign",
        target: {
          product: presentationThemeProduct.id,
          campaign: presentationThemeProduct.fredhopperCampaignId,
        },
        sourceId: presentationThemeProduct.fredhopperResponseId,
      };
      pushToTheDataLayer(event);
    },
    [pushToTheDataLayer]
  );

  const clickActionOnFacet: NonNullable<
    ReactPlpAnalyticsInterface["clickActionOnFacet"]
  > = React.useCallback(
    ({ facet, option, wasTheOptionOnWhenTheUserClickedOnIt, isMobile }) => {
      const event = {
        event: "plp_filter_interaction",
        plp: {
          breakpoint: isMobile ? "mobile" : "desktop",
          filter_category: facet.title,
          filter_type: facet.hasMultiSelection
            ? "multi-select"
            : "single-select",
          filter_selected: option.name,
          filter_selected_count: option.count,
          filter_selected_action_direction: wasTheOptionOnWhenTheUserClickedOnIt
            ? "off"
            : "on",
          sourceId: facet.fredhopperResponseId,
        },
      };
      pushToTheDataLayer(event);
    },
    [pushToTheDataLayer]
  );

  const clickProductColorSwatch: NonNullable<
    ReactPlpAnalyticsInterface["clickProductColorSwatch"]
  > = React.useCallback(
    ({
      fredhopper_magento_id_for_the_whole_item,
      color_swatch_selected_value,
      corresponding_grouped_product_name_for_color_swatch,
    }) => {
      const event = {
        event: "plp_product_swatch_interaction",
        plp: {
          fredhopper_magento_id_for_the_whole_item,
          color_swatch_selected_value,
          corresponding_grouped_product_name_for_color_swatch,
        },
      };
      pushToTheDataLayer(event);
    },
    [pushToTheDataLayer]
  );

  const actionPlpSortingOption: NonNullable<
    ReactPlpAnalyticsInterface["actionPlpSortingOption"]
  > = React.useCallback(
    ({ sortingOption }) => {
      const event = {
        event: "plp_sorting_interaction",
        plp: {
          sorting_option: sortingOption,
        },
      };
      pushToTheDataLayer(event);
    },
    [pushToTheDataLayer]
  );

  const initializedInterface: ReactPlpAnalyticsInterface = React.useMemo(
    () => ({
      onViewFredhopperListingPage,
      clickActionOnProduct,
      clickActionOnProductFromCampaign,
      clickActionOnFacet,
      clickProductColorSwatch,
      actionPlpSortingOption,
    }),
    [
      onViewFredhopperListingPage,
      clickActionOnProduct,
      clickActionOnProductFromCampaign,
      clickActionOnFacet,
      clickProductColorSwatch,
      actionPlpSortingOption,
    ]
  );
  const analyticsInterface = consumerInitializationGuard({
    initializedInterface,
  });
  return analyticsInterface;
};
